import { memo, useMemo } from "react";

import { COLLECTION_FILTER_IDS } from "@react/sections/Collection/CollectionFilters/constants";
import { useCollectionFilters } from "@react/sections/Collection/CollectionFiltersContext";
import { CollectionGridEmpty } from "@react/sections/Collection/CollectionGrid/CollectionGridEmpty.view";
import { CollectionGridPlaceholder } from "@react/sections/Collection/CollectionGrid/CollectionGridPlaceholder.view";
import { filterProductByVariantEligibility } from "@react/utilities/collection";
import {
  type CollectionContentCard as CollectionContentCardType,
  type OPProductConnection,
} from "@tablescape/state";

import { CollectionGridView } from "./CollectionGrid.view";

type Props = {
  bundleValueText: string;
  contentCards: Array<{
    contentCard: CollectionContentCardType;
    id: string;
    position: string;
  }> | null;
  emptyStateText: string;
  enableQuickAdd?: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  nextLabel: string;
  pageSize: number;
  pageType: string;
  prevLabel: string;
  productResults: Array<OPProductConnection>;
  quickAddButtonText?: string;
  variantMatchCollectionName?: string;
};

const CollectionGridBase = ({
  bundleValueText,
  contentCards,
  emptyStateText,
  enableQuickAdd,
  isLoading,
  isLoadingMore,
  nextLabel,
  pageSize,
  pageType,
  prevLabel,
  productResults,
  quickAddButtonText,
  variantMatchCollectionName,
}: Props) => {
  const { appliedFilters, appliedSort, availableSorts } =
    useCollectionFilters();

  // Extract color filters for passing to child components (used for color prioritization)
  const appliedColorFilters = useMemo(
    () =>
      appliedFilters.filter((filter) =>
        filter.id.startsWith(COLLECTION_FILTER_IDS.COLOR)
      ),
    [appliedFilters]
  );

  const variantEligibleProductResults = useMemo(() => {
    return productResults.filter((product) =>
      filterProductByVariantEligibility(
        product,
        variantMatchCollectionName,
        appliedFilters
      )
    );
  }, [productResults, variantMatchCollectionName, appliedFilters]);

  const showContentCards = useMemo(
    () =>
      !!contentCards &&
      appliedFilters.length === 0 &&
      (appliedSort === undefined ||
        appliedSort?.name === availableSorts[0].name),
    [contentCards, appliedFilters.length, appliedSort, availableSorts]
  );

  return !isLoadingMore && isLoading ? (
    <CollectionGridPlaceholder pageSize={pageSize} />
  ) : variantEligibleProductResults.length === 0 ? (
    <CollectionGridEmpty emptyStateText={emptyStateText} />
  ) : (
    <CollectionGridView
      appliedColorFilters={appliedColorFilters}
      bundleValueText={bundleValueText}
      contentCards={contentCards}
      enableQuickAdd={enableQuickAdd}
      isLoadingMore={isLoadingMore}
      nextLabel={nextLabel}
      pageSize={pageSize}
      pageType={pageType}
      prevLabel={prevLabel}
      productResults={variantEligibleProductResults}
      quickAddButtonText={quickAddButtonText}
      showContentCards={showContentCards}
      variantMatchCollectionName={variantMatchCollectionName}
    />
  );
};

export const CollectionGrid = memo(CollectionGridBase);
